import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, timeAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import PunchInOutWidget from '../components/PunchInOutWidget';
import HRDashboard from './HRDashboard';
import OperationsHeadDashboard from './OperationsHeadDashboard';
import ProjectManagerDashboard from './ProjectManagerDashboard';
import ProjectLeadDashboard from './ProjectLeadDashboard';
import { toast } from 'sonner';
import './Dashboard.css';

// Line Icons Component
const Icon = ({ name, size = 20 }) => {
  const icons = {
    checkCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    activity: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
    code: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>,
    alertCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
    play: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>,
  };
  return icons[name] || null;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [productivity, setProductivity] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showStopTimerModal, setShowStopTimerModal] = useState(false);
  const [taskWorkNotes, setTaskWorkNotes] = useState('');
  const [stats, setStats] = useState({
    totalTasks: 0,
    inProgress: 0,
    completed: 0,
    toDo: 0,
    blocked: 0,
    inReview: 0,
    hoursLogged: 0,
    todayHours: 0,
    weekHours: 0,
    bugFixes: 0,
    testingTasks: 0,
    newDevelopment: 0,
    overdueTasks: 0,
    criticalBugs: 0,
    highPriorityTasks: 0,
    isPunchedIn: false
  });

  // Redirect based on role
  if (user?.role === 'HR') return <HRDashboard />;
  if (user?.role === 'Operations Head') return <OperationsHeadDashboard />;
  if (user?.role === 'Project Manager') return <ProjectManagerDashboard />;
  if (user?.role === 'Project Lead') return <ProjectLeadDashboard />;

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    filterTasks(activeFilter);
  }, [tasks, activeFilter]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, tasksRes, productivityRes, activityRes] = await Promise.all([
        dashboardAPI.getDeveloperStats(),
        dashboardAPI.getDeveloperTasks(),
        dashboardAPI.getDeveloperProductivity(7),
        dashboardAPI.getDeveloperActivity(5)
      ]);

      setStats(statsRes.data.data);
      setTasks(tasksRes.data.data || []);
      setProductivity(productivityRes.data.data || []);
      setRecentActivity(activityRes.data.data || []);

      if (statsRes.data.data.activeTimer) {
        setActiveTimer(statsRes.data.data.activeTimer.taskId);
      }

      setError(null);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      setError('Unable to load dashboard. Please check if backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const filterTasks = (filter) => {
    if (filter === 'All') {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(tasks.filter(t => t.status === filter));
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleStartTimer = async (taskId, projectId) => {
    if (activeTimer) {
      toast.warning('Please stop the current timer before starting a new one');
      return;
    }
    try {
      await timeAPI.startTimer({ taskId, projectId });
      setActiveTimer(taskId);
      toast.success('Timer started');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start timer');
    }
  };

  const handleStopTimer = () => {
    setShowStopTimerModal(true);
  };

  const executeStopTimer = async () => {
    if (!taskWorkNotes.trim()) {
      toast.warning('Please enter work notes before stopping the timer');
      return;
    }

    try {
      await timeAPI.stopTimer({ workNotes: taskWorkNotes });
      setActiveTimer(null);
      setShowStopTimerModal(false);
      setTaskWorkNotes('');
      toast.success('Timer stopped successfully');
      await loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to stop timer');
    }
  };

  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const diff = Math.floor((today - d) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    return d.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'done': return 'success';
      case 'in progress': return 'progress';
      case 'in review': return 'review';
      case 'blocked': return 'blocked';
      default: return 'todo';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'high';
      case 'medium': return 'medium';
      default: return 'low';
    }
  };

  const getTaskTypeColor = (type) => {
    if (type === 'Bug Fix') return 'bug';
    if (type === 'Feature Development') return 'feature';
    return 'default';
  };

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Dashboard</h1>
            <p className="dashboard-subtitle">Welcome back, {user?.name || 'Developer'}</p>
          </div>
        </div>

        <PunchInOutWidget />

        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-icon">
              <Icon name="activity" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalTasks}</div>
              <div className="stat-label">Total Tasks</div>
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon">
              <Icon name="clock" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.inProgress}</div>
              <div className="stat-label">In Progress</div>
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-icon">
              <Icon name="checkCircle" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.completed}</div>
              <div className="stat-label">Completed</div>
            </div>
          </div>
          {user?.role === 'QA Engineer' ? (
            <>
              <div className="stat-card bug">
                <div className="stat-icon">
                  <Icon name="alertCircle" size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.bugFixes}</div>
                  <div className="stat-label">Bug Fixes</div>
                </div>
              </div>
              <div className="stat-card feature">
                <div className="stat-icon">
                  <Icon name="code" size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.testingTasks}</div>
                  <div className="stat-label">Testing Tasks</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="stat-card bug">
                <div className="stat-icon">
                  <Icon name="alertCircle" size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.bugFixes}</div>
                  <div className="stat-label">Bug Fixes</div>
                </div>
              </div>
              <div className="stat-card feature">
                <div className="stat-icon">
                  <Icon name="code" size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.newDevelopment}</div>
                  <div className="stat-label">New Development</div>
                </div>
              </div>
            </>
          )}
        </div>

        {stats.criticalBugs > 0 && (
          <div className="alert-banner critical">
            <Icon name="alertCircle" size={20} />
            <span>⚠️ {stats.criticalBugs} critical bug{stats.criticalBugs > 1 ? 's' : ''} require{stats.criticalBugs === 1 ? 's' : ''} immediate attention!</span>
          </div>
        )}

        {stats.overdueTasks > 0 && (
          <div className="alert-banner overdue">
            <Icon name="alertCircle" size={20} />
            <span>You have {stats.overdueTasks} overdue task{stats.overdueTasks > 1 ? 's' : ''}. Please review and update.</span>
          </div>
        )}

        {stats.inReview > 0 && user?.role === 'QA Engineer' && (
          <div className="alert-banner info">
            <Icon name="clock" size={20} />
            <span>📋 {stats.inReview} task{stats.inReview > 1 ? 's are' : ' is'} pending review</span>
          </div>
        )}

        <div className="dashboard-grid">
          <div className="dashboard-main">
            <div className="productivity-chart">
              <div className="section-header">
                <div className="section-title">
                  <Icon name="activity" size={20} />
                  <h2>Weekly Productivity</h2>
                </div>
                <div className="chart-legend">
                  <span className="legend-item">
                    <span className="legend-dot primary"></span>
                    Total hours worked
                  </span>
                </div>
              </div>
              {productivity.length === 0 || productivity.every(day => day.hours === 0) ? (
                <div className="empty-productivity">
                  <Icon name="activity" size={32} />
                  <p>No work logged this week</p>
                  <span className="empty-hint">Start tracking time to see your productivity</span>
                </div>
              ) : (
                <div className="chart-container">
                  {productivity.map((day, idx) => {
                    const maxHours = Math.max(...productivity.map(d => d.hours), 8);
                    const height = day.hours > 0 ? Math.max((day.hours / maxHours) * 100, 8) : 0;
                    const dayName = new Date(day.date).toLocaleDateString('en', { weekday: 'short' });
                    return (
                      <div key={idx} className="chart-bar-wrapper">
                        {day.hours > 0 ? (
                          <div className="chart-bar" style={{ height: `${height}%` }} title={`${day.hours}h worked on ${dayName}`}>
                            <span className="bar-value">{day.hours}h</span>
                          </div>
                        ) : (
                          <div className="chart-bar-empty" title={`No work logged on ${dayName}`}>
                            <span className="bar-value-empty">-</span>
                          </div>
                        )}
                        <span className="bar-label">{dayName}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="tasks-section">
              <div className="section-header">
                <div className="section-title">
                  <Icon name="activity" size={20} />
                  <h2>My Tasks</h2>
                </div>
                <div className="task-filters">
                  <button
                    className={`filter-btn ${activeFilter === 'All' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('All')}
                  >
                    All
                  </button>
                  <button
                    className={`filter-btn ${activeFilter === 'To Do' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('To Do')}
                  >
                    To Do
                  </button>
                  <button
                    className={`filter-btn ${activeFilter === 'In Progress' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('In Progress')}
                  >
                    In Progress
                  </button>
                  {user?.role === 'QA Engineer' && (
                    <button
                      className={`filter-btn ${activeFilter === 'In Review' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('In Review')}
                    >
                      In Review
                    </button>
                  )}
                  <button
                    className={`filter-btn ${activeFilter === 'Done' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('Done')}
                  >
                    Done
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading tasks...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p>{error}</p>
                  <button className="btn-retry" onClick={loadDashboardData}>
                    Retry
                  </button>
                </div>
              ) : (
                <div className="tasks-grid">
                  {filteredTasks.length === 0 ? (
                    <div className="empty-state">
                      <Icon name="activity" size={32} />
                      <p>No tasks found</p>
                    </div>
                  ) : (
                    filteredTasks.map(task => (
                      <div key={task._id} className={`task-card ${activeTimer === task._id ? 'active-task' : ''}`}>
                        <div className="task-header">
                          <h3 className="task-title">{task.title}</h3>
                          <span className={`task-status status-${getStatusColor(task.status)}`}>
                            {task.status || 'To Do'}
                          </span>
                        </div>

                        <p className="task-description">{task.description || 'No description'}</p>

                        <div className="task-meta">
                          <span className={`task-type type-${getTaskTypeColor(task.type)}`}>
                            {task.type || 'Task'}
                          </span>
                          <span className="task-hours">{task.estimatedHours || 0}h estimated</span>
                          {task.priority && (
                            <span className={`task-priority priority-${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                          )}
                        </div>

                        <div className="task-footer">
                          {task.project && (
                            <span className="task-project">Project: {task.project.name || 'Unnamed'}</span>
                          )}
                          <div className="task-actions">
                            {activeTimer === task._id ? (
                              <button className="btn-task-action danger" onClick={handleStopTimer}>
                                Stop Timer
                              </button>
                            ) : (
                              task.status !== 'Done' && (
                                <button
                                  className="btn-task-action primary"
                                  onClick={() => handleStartTimer(task._id, task.project?._id)}
                                  disabled={activeTimer && activeTimer !== task._id}
                                >
                                  <Icon name="play" size={16} />
                                  Start Timer
                                </button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-sidebar">
            <div className="quick-stats-card">
              <h3>Quick Stats</h3>
              <div className="quick-stat-item">
                <span className="quick-stat-label">Week Hours</span>
                <span className="quick-stat-value">{formatTime(parseFloat(stats.weekHours))}</span>
              </div>
              <div className="quick-stat-item">
                <span className="quick-stat-label">Total Hours</span>
                <span className="quick-stat-value">{formatTime(parseFloat(stats.hoursLogged))}</span>
              </div>
              <div className="quick-stat-item">
                <span className="quick-stat-label">To Do</span>
                <span className="quick-stat-value">{stats.toDo}</span>
              </div>
              {user?.role === 'QA Engineer' && (
                <div className="quick-stat-item">
                  <span className="quick-stat-label">In Review</span>
                  <span className="quick-stat-value review">{stats.inReview}</span>
                </div>
              )}
              <div className="quick-stat-item">
                <span className="quick-stat-label">Blocked</span>
                <span className="quick-stat-value blocked">{stats.blocked}</span>
              </div>
              {user?.role === 'QA Engineer' && stats.criticalBugs > 0 && (
                <div className="quick-stat-item">
                  <span className="quick-stat-label">Critical Bugs</span>
                  <span className="quick-stat-value critical">{stats.criticalBugs}</span>
                </div>
              )}
              {stats.highPriorityTasks > 0 && (
                <div className="quick-stat-item">
                  <span className="quick-stat-label">High Priority</span>
                  <span className="quick-stat-value high">{stats.highPriorityTasks}</span>
                </div>
              )}
            </div>

            <div className="recent-activity-card">
              <h3>Recent Activity</h3>
              {recentActivity.length === 0 ? (
                <p className="no-activity">No recent activity</p>
              ) : (
                <div className="activity-list">
                  {recentActivity.map((activity, idx) => (
                    <div key={idx} className="activity-item">
                      <div className="activity-icon">
                        <Icon name="clock" size={16} />
                      </div>
                      <div className="activity-content">
                        <p className="activity-task">{activity.task}</p>
                        <p className="activity-meta">
                          {activity.duration}h • {formatDate(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {activeTimer && (
          <div className="active-timer-banner">
            <div className="timer-info">
              <span className="timer-pulse"></span>
              <span>Timer Running - {stats.activeTimer?.taskTitle || 'Task in Progress'}</span>
            </div>
            <button className="btn-stop-timer" onClick={handleStopTimer}>
              Stop Timer
            </button>
          </div>
        )}

        {showStopTimerModal && (
          <div className="modal-overlay" onClick={() => setShowStopTimerModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-simple">
                <Icon name="clock" size={24} />
                <h3>Stop Task Timer?</h3>
              </div>

              <p className="modal-description">
                Please provide a brief summary of what you accomplished during this session.
              </p>

              <div className="notes-input-wrapper">
                <textarea
                  className="notes-input"
                  placeholder="Commits, modules delivered, blockers encountered..."
                  value={taskWorkNotes}
                  onChange={(e) => setTaskWorkNotes(e.target.value)}
                  autoFocus
                ></textarea>
              </div>

              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowStopTimerModal(false);
                    setTaskWorkNotes('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-confirm btn-stop-timer-confirm"
                  onClick={executeStopTimer}
                  disabled={!taskWorkNotes.trim()}
                >
                  Complete Session
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
